import type { ExecutionContext } from './context/execution-context'
import { MethodInput } from './context/method-input'
import type { Extension } from './extension'
import type { SharedExtensionMethod } from './model/model-types'

// One callable method of an extension. Every kind — action, dictionary, generated loader, SYSTEM seam —
// answers the same three questions, which is the whole dispatch and model-build contract:
//
//   toModel()          what the console/flow-engine sees in `methodsMap`
//   validate(input)    what this kind accepts, and how a bad call fails
//   execute(params)    what running it does
//
// So dispatch is a map lookup plus `execute(validate(input))`, and building the definition is one pass over
// `extension.methods()`. Adding a kind means adding a subclass, not editing a chain of `if`s.

export abstract class ExtensionMethod {
  constructor(protected readonly extension: Extension) {}

  // The published method name — the key in `methodsMap`, and what the flow engine invokes.
  abstract get name(): string

  // The v1 kind token: ACTION | DICTIONARY | POLLING_TRIGGER | REALTIME_TRIGGER | SYSTEM |
  // SAMPLE_RESULT_LOADER | PARAM_SCHEMA_DEFINITION.
  abstract get registerAs(): string

  abstract toModel(): SharedExtensionMethod

  // Extra OAuth scopes this method needs. Only the author-declared kinds can carry any; the generated
  // loaders and the SYSTEM seams never do.
  get oauth2Scopes(): string[] {
    return []
  }

  /**
   * Whether this method appears in the published `methodsMap`. Almost always yes — but the realtime refresh
   * seam is callable whether or not the author declared a `refresh` handler (the runtime answers `{}` when
   * they did not), while only being ADVERTISED when they did. Callable and advertised are therefore two
   * different questions, and this is the second one.
   */
  get published(): boolean {
    return true
  }

  // Turn raw `methodData` into whatever `execute` takes. The default accepts anything — a kind with no
  // declared input has nothing to check.
  validate(methodData: Record<string, unknown>): unknown {
    return methodData
  }

  // May return a promise or a plain value; the caller awaits either way, so a synchronous handler is not
  // forced through an async wrapper.
  abstract execute(params: unknown, ctx: ExecutionContext): unknown

  protected input(methodData: Record<string, unknown>): MethodInput {
    return new MethodInput(this.extension.id, this.name, methodData)
  }
}
